import type { AxiosStatic, AxiosInstance, AxiosRequestConfig } from 'axios';
import { AxiosError } from 'axios';
import {
    HttpClientError,
    IHttpClient,
    IRequestOptions,
    IRequestOptionsBase,
    IRequestResult,
} from './IHttpClient';
import { HeadersGetter, IHttpClientConfig } from './IHttpClientConfig';

export class AxiosHttpClient implements IHttpClient {
    private headersGetter?: HeadersGetter;
    private axios: AxiosStatic;
    private instance: AxiosInstance;

    constructor(axios: AxiosStatic, config: IHttpClientConfig) {
        this.headersGetter = config.headersGetter;
        this.axios = axios;
        this.instance = axios.create({
            baseURL: config?.baseUrl,
        });
    }

    async request<TResult, TData = any>(
        config: IRequestOptions<TData>,
    ): Promise<IRequestResult<TResult>> {
        const { abortController, ...rest } = config;
        const finalHeaders = { ...rest.headers, ...this.headersGetter?.() };
        const axiosConfig: AxiosRequestConfig = {
            ...rest,
            headers: finalHeaders,
        };

        let controller: AbortController | undefined;
        let unregister: (() => void) | undefined;

        if (abortController) {
            controller = new AbortController();
            unregister = abortController.onAbort.reo(() => {
                controller!.abort();
            });

            axiosConfig.signal = controller.signal;
        }

        try {
            const response = await this.instance.request<TResult>(axiosConfig);

            return {
                data: response.data,
                headers: response.headers as Record<string, string>,
                status: response.status,
            };
        } catch (error) {
            const { AxiosError } = this.axios;
            const axiosError = error as AxiosError;

            if (axiosError.isAxiosError) {
                throw new HttpClientError(
                    axiosError.code,
                    axiosError.message,
                    axiosError.response?.headers as Record<string, string>,
                    axiosError.code === AxiosError.ERR_CANCELED,
                    axiosError.status,
                );
            } else {
                throw error;
            }
        } finally {
            unregister?.();
        }
    }

    get<TResult>(url: string, options?: IRequestOptionsBase): Promise<IRequestResult<TResult>> {
        return this.request({
            url,
            method: 'GET',
            ...options,
        });
    }
    async post<TResult, TData = any>(
        url: string,
        data: TData,
        options?: IRequestOptionsBase,
    ): Promise<IRequestResult<TResult>> {
        return this.request({
            url,
            method: 'POST',
            data,
            ...options,
        });
    }
    async put<TResult, TData = any>(
        url: string,
        data: TData,
        options?: IRequestOptionsBase,
    ): Promise<IRequestResult<TResult>> {
        return this.request({
            url,
            method: 'PUT',
            data,
            ...options,
        });
    }
    async delete<TResult>(
        url: string,
        options?: IRequestOptionsBase,
    ): Promise<IRequestResult<TResult>> {
        return this.request({
            url,
            method: 'DELETE',
            ...options,
        });
    }
}
